import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Users,
  BookOpen,
  Clock,
} from "lucide-react";
import { eCourseService } from "../../services/elearning/eCourseService";
import type { ECourse } from "../../services/elearning/eCourseService";
import { CreateCourseModal } from "../../components/elearning/CreateCourseModal";
import { useAuth } from "../../hooks/useAuth";
import toast from "react-hot-toast";
import Pagination from "../../components/common/Pagination";

export const ECourses: React.FC = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<ECourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalCourses, setTotalCourses] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response = await eCourseService.getTeacherCourses(params);
      console.log("API Response:", response);
      if (response.success) {
        setCourses(response.data.courses || []);
        setTotalCourses(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setError("Failed to fetch courses");
      }
    } catch (err) {
      console.error("Error fetching courses:", err);
      setError("Failed to fetch courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [statusFilter, currentPage, itemsPerPage]);

  const handleCreateCourse = async (courseData: any) => {
    setIsSubmitting(true);
    try {
      const response = await eCourseService.createCourse(courseData);
      if (response.success) {
        setCourses((prev) => [response.data, ...prev]);
        setShowCreateModal(false);
        toast.success(
          "Course created successfully! It has been submitted for admin approval. You will be notified once it's reviewed."
        );
      } else {
        toast.error(response.message || "Failed to create course");
      }
    } catch (err: any) {
      console.error("Error creating course:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to create course"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublishCourse = async (courseId: string) => {
    try {
      const response = await eCourseService.publishCourse(courseId);
      if (response.success) {
        setCourses((prev) =>
          prev.map((course) =>
            course._id === courseId
              ? { ...course, status: "published" as const }
              : course
          )
        );
      } else {
        setError("Failed to publish course");
      }
    } catch (err) {
      console.error("Error publishing course:", err);
      setError("Failed to publish course");
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (!confirm("Are you sure you want to delete this course?")) return;

    try {
      const response = await eCourseService.deleteCourse(courseId);
      if (response.success) {
        setCourses((prev) => prev.filter((course) => course._id !== courseId));
      } else {
        setError("Failed to delete course");
      }
    } catch (err) {
      console.error("Error deleting course:", err);
      setError("Failed to delete course");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "published":
        return "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white";
      case "approved":
        return "bg-gradient-to-br from-blue-300 to-indigo-400 text-white";
      case "pending_approval":
        return "bg-gradient-to-br from-amber-300 to-yellow-400 text-white";
      case "rejected":
      case "suspended":
        return "bg-gradient-to-br from-red-300 to-pink-400 text-white";
      case "draft":
        return "bg-gradient-to-br from-amber-300 to-yellow-400 text-white";
      case "archived":
        return "bg-gradient-to-br from-slate-300 to-slate-400 text-white";
      default:
        return "bg-gradient-to-br from-slate-300 to-slate-400 text-white";
    }
  };

  const filteredCourses = courses.filter(
    (course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* Error State */}
      {error && (
        <div className="mb-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-red-200/60 shadow-xl p-6">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-red-300 to-pink-400 rounded-xl p-3 shadow-lg">
              <span className="text-white text-xl">⚠</span>
            </div>
            <p className="text-red-800 font-medium flex-1">{error}</p>
            <button
              onClick={fetchCourses}
              className="bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-4 py-2 rounded-xl font-bold hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My E-Courses</h1>
            <p className="text-slate-600 mt-1 font-medium">
              Create and manage your e-learning courses
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <Plus size={20} />
            Create Course
          </button>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="published">Published</option>
            <option value="rejected">Rejected</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Course Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-emerald-500"></div>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
          <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
            <BookOpen className="text-white" size={32} />
          </div>
          <h3 className="mt-2 text-lg font-bold text-slate-900">
            No courses found
          </h3>
          <p className="mt-1 text-sm text-slate-600 font-medium">
            {courses.length === 0
              ? "Get started by creating your first e-course."
              : "Try adjusting your search or filter criteria."}
          </p>
          {courses.length === 0 && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Create Your First Course
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course) => (
            <div
              key={course._id}
              className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <div className="p-6">
                {/* Status Badge */}
                <div className="flex justify-between items-start mb-3">
                  <span
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold shadow-sm ${getStatusColor(
                      course.status || "draft"
                    )}`}
                  >
                    {(course.status || "draft").charAt(0).toUpperCase() +
                      (course.status || "draft").slice(1).replace("_", " ")}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() =>
                        (window.location.href = `/elearning/courses/${course._id}`)
                      }
                      className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-lg p-1.5 text-white hover:scale-110 transition-all duration-300 shadow-sm"
                      title="View Course"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() =>
                        (window.location.href = `/elearning/courses/${course._id}/edit`)
                      }
                      className="bg-gradient-to-br from-amber-300 to-yellow-400 rounded-lg p-1.5 text-white hover:scale-110 transition-all duration-300 shadow-sm"
                      title="Edit Course"
                    >
                      <Edit size={16} />
                    </button>
                    {user?.roles?.includes("admin") ||
                    user?.roles?.includes("superadmin") ? (
                      <button
                        onClick={() => handleDeleteCourse(course._id)}
                        className="bg-gradient-to-br from-red-300 to-pink-400 rounded-lg p-1.5 text-white hover:scale-110 transition-all duration-300 shadow-sm"
                        title="Delete Course"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : null}
                  </div>
                </div>

                {/* Course Info */}
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {course.title}({course.level})
                </h3>
                <p className="text-slate-600 text-sm font-medium mb-4 line-clamp-2">
                  {course.shortDescription || course.description}
                </p>

                {/* Course Meta */}
                <div className="space-y-2.5 text-sm text-slate-600 font-medium">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-lg p-1.5 shadow-sm">
                      <Users size={16} className="text-white" />
                    </div>
                    <span>{course.stats?.totalStudents || 0} students</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-amber-300 to-yellow-400 rounded-lg p-1.5 shadow-sm">
                      <Clock size={16} className="text-white" />
                    </div>
                    <span>
                      {course.duration?.estimatedHours || 0}h{" "}
                      {course.duration?.estimatedMinutes || 0}m
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-lg p-1.5 shadow-sm">
                      <BookOpen size={16} className="text-white" />
                    </div>
                    <span>
                      {course.level || "Beginner"} •{" "}
                      {course.category || "Other"}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 flex flex-col gap-2">
                  {course.status === "pending_approval" && (
                    <div className="bg-gradient-to-br from-amber-300 to-yellow-400 text-white px-3 py-2 rounded-xl text-sm text-center font-bold shadow-sm">
                      Pending Admin Approval
                    </div>
                  )}
                  {course.status === "rejected" && (
                    <div className="bg-gradient-to-br from-red-300 to-pink-400 text-white px-3 py-2 rounded-xl text-sm text-center font-bold shadow-sm">
                      Rejected - Contact Admin
                    </div>
                  )}
                  {course.status === "approved" && (
                    <button
                      onClick={() => handlePublishCourse(course._id)}
                      className="bg-gradient-to-br from-emerald-300 to-emerald-400 text-white px-3 py-2 rounded-xl text-sm font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      Publish Course
                    </button>
                  )}
                  {course.status === "draft" && (
                    <button
                      onClick={() => handlePublishCourse(course._id)}
                      className="bg-gradient-to-br from-emerald-300 to-emerald-400 text-white px-3 py-2 rounded-xl text-sm font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      Publish
                    </button>
                  )}
                  {(course.status === "approved" ||
                    course.status === "published" ||
                    course.status === "draft") && (
                    <button
                      onClick={() =>
                        (window.location.href = `/elearning/courses/${course._id}/manage`)
                      }
                      className="bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-3 py-2 rounded-xl text-sm font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      Manage Content
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {courses.length > 0 && (
        <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={itemsPerPage}
            onPageSizeChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
            showPageSizeSelector={true}
            className="w-full"
          />
        </div>
      )}

      {/* Create Course Modal */}
      {showCreateModal && (
        <CreateCourseModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateCourse}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};
